import { forwardRef, useEffect, useMemo, useRef } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { MDBRow, MDBCol } from "mdbreact";
import { useParams } from "react-router-dom";

import "./FeuD2EstimateEngeryConsumption.scss";

import { Button, Label, TextArea } from "abs-ui-components";

import { FuelEuDCommonTableColumns } from "../../FeuConstants";
import FormField from "../../../../../common/form/Form";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import { API_ROUTES } from "../../FeuApiRoutes/FeuApiRoutes";
import AutoSave from "../../../../../common/AutoSave/AutoSave";
import { AUTOSAVE_SUCCESS_MESSAGE } from "../../../Mrv/Constants";

const FeuD2EstimateEngeryConsumption = forwardRef(({ D2FeuEstimateEngeryConsumptionData, refetchMonitoringData, dropdownData,vesselMstId  }, ref) => {
    const { control, getValues, reset, formState } = useFormContext();
    const { isDirty } = formState;
    const lastManualSave = useRef(null);
    const manualSaveTriggered = useRef(false);

    useEffect(() => {
        if (D2FeuEstimateEngeryConsumptionData) {
            reset(prev => ({ ...prev, d2: D2FeuEstimateEngeryConsumptionData }));
        }
    }, [D2FeuEstimateEngeryConsumptionData, reset]);

    AutoSave(ref, [], async () => {
        if (!isDirty) return;
        await handleSave(true);
    });

    const handleSave = async (isAuto = false) => {
        const formValues = getValues("d2");
        const payload = {
            ...d2FeuFields.reduce((acc, { name }) => {
                acc[name] = formValues?.[name] ?? "";
                return acc;
            }, {}),
        }
        try {
            const D2FeuEstimateEngeryConsumption = await callApi({
                method: "POST",
                url: API_ROUTES.D2(vesselMstId),
                data: payload,
            });
            if (D2FeuEstimateEngeryConsumption?.isSuccess) {
                emitToasterMsg("success", "Table D.2", isAuto ? AUTOSAVE_SUCCESS_MESSAGE : "Created successfully");
                reset(prev => ({ ...prev, d2: formValues }));
                if (!isAuto) {
                    lastManualSave.current = Date.now();
                }
                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }
            }
            else {
                emitToasterMsg("error", "Table D.2", D2FeuEstimateEngeryConsumption?.errorMessage);
            }
        } catch (error) {
            emitToasterMsg("error", "Table D.2", error?.errorMessage);
        }
    };

    const monitoringMethodOptions = useMemo(() => {
        if (!dropdownData?.monitoringMethodOptions?.length) return [];
        return [
            { label: "Select method", value: "" },
            ...dropdownData.monitoringMethodOptions.map(({ name, code }) => ({
                label: name,
                value: code,
            })),
        ];
    }, [dropdownData]);

    const d2FeuFields = useMemo(() => {
        const base = FuelEuDCommonTableColumns.filter(f => f.name !== "methodDescription");
        base.splice(2, 0, {
            label: "Description of method to estimate energy consumption",
            name: "methodDescription",
            maxLength: 4000,
        });
        return base;
    }, []);

    return (
        <div className="d2-section-form mt-4">
            <MDBRow className="mb-2">
                <MDBCol md="4">
                    <h6 className="titles-font">Title of method</h6>
                </MDBCol>
                <MDBCol md="4">
                    <h6 className="titles-font">
                        Method to be used to estimate energy consumption
                    </h6>
                </MDBCol>
            </MDBRow>

            {d2FeuFields.map(({ label, name, maxLength, type }, index) => (
                <MDBRow className="align-items-center mb-3" key={index}>
                    <MDBCol md="4">
                        <Label name={label} bold />
                    </MDBCol>
                    <MDBCol md="4">
                        {type === "dropdown" ? (
                            <FormField
                                name={`d2.${name}`}
                                type="dropdown"
                                options={monitoringMethodOptions}
                            />
                        ) : (
                            <Controller
                                name={`d2.${name}`}
                                control={control}
                                render={({ field }) => (
                                    <TextArea
                                        id={`d2_${name}`}
                                        className="textarea-resize"
                                        value={field.value || ""}
                                        onChange={field.onChange}
                                        placeholder=""
                                        maxLength={maxLength}
                                        rows={1}
                                    />
                                )}
                            />
                        )}
                    </MDBCol>
                </MDBRow>
            ))}
            <div className="text-center">
                <Button id="feud2_save_btn" disabled={!isDirty} onClick={async () => {
                    manualSaveTriggered.current = true;
                    await handleSave(false);
                }}>
                    Save
                </Button>
            </div>
        </div>
    );
});

export default FeuD2EstimateEngeryConsumption;
